import { Anchor, Button, Checkbox, Group, Input, Stack, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconDeviceTablet, IconSignature, IconWritingSign } from '@tabler/icons-react'
import { fileUrl, formatDateTime } from '@tnn/core'
import { keepable, keepableSignatureMethods, type Signature } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { SectionCard, StatusPill } from '@tnn/ui'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { appLink } from '../app/links'
import { useScreenGate } from '../screen/screen'
import { HandoffStatus, useHandoff } from '../tasks/handoff'
import { type SignatureChoiceValue, useForgetSignature, useKeepSignature, useMySignature } from './api'
import { SignatureDialog, SignatureImage, useBlobUrl } from './capture'
import classes from './signature.module.css'

/**
 * Signing on a form (agreeing to something): one's kept signature in one tap, or signed there and then — kept as one's
 * own too unless unticked when there is none yet. Nothing is chosen until the person chooses. At a computer one may
 * sign on one's portable screen instead (a mouse makes a poor pen): the box waits, then shows it.
 */
export function SignatureChoice({
  value,
  onChange,
  label,
  dialogTitle,
  intro,
  error,
}: {
  value: SignatureChoiceValue | null
  onChange: (value: SignatureChoiceValue | null) => void
  label?: ReactNode
  dialogTitle: ReactNode
  /** What is being agreed to, shown again above the signing area */
  intro?: ReactNode
  error?: ReactNode
}) {
  const { t } = useTranslation()
  const kept = useMySignature().data
  const [signing, setSigning] = useState(false)
  const fresh = useBlobUrl(value?.kind === 'new' ? value.signature.blob : null)
  const touch = useMediaQuery('(pointer: coarse)')
  const handoff = useHandoff({
    kind: 'signature',
    label: typeof dialogTitle === 'string' ? dialogTitle : t('task.signature'),
    onDone: (task) => {
      const method = task.signatureMethod ?? 'drawn'
      if (task.fileIds[0]) onChange({ kind: 'uploaded', fileId: task.fileIds[0], method, keep: !kept && keepable(method) })
    },
  })
  const gate = useScreenGate(handoff.available)
  // On a touch screen one signs right here: one's screen is offered only once there is one
  const onScreen =
    handoff.available && handoff.myId && (!touch || gate.screen) ? (
      <Button
        size="md"
        variant="default"
        leftSection={<IconDeviceTablet size={20} />}
        onClick={() =>
          gate.run(() => {
            if (handoff.myId) handoff.ask(handoff.myId).catch(() => notifications.show({ color: 'red', message: t('task.failed') }))
          })
        }
      >
        {t('screen.signThere')}
      </Button>
    ) : null
  const method = value?.kind === 'new' ? value.signature.method : value?.kind === 'uploaded' ? value.method : null
  const dialog = (
    <SignatureDialog
      opened={signing}
      onClose={() => setSigning(false)}
      title={dialogTitle}
      intro={intro}
      onConfirm={(signature) => onChange({ kind: 'new', signature, keep: !kept && keepable(signature.method) })}
    />
  )

  return (
    <Input.Wrapper label={label} error={error}>
      <div className={classes.choice}>
        {value ? (
          <>
            <SignatureImage
              fileId={value.kind === 'kept' ? kept?.fileId : value.kind === 'uploaded' ? value.fileId : undefined}
              src={value.kind === 'new' ? fresh : undefined}
            />
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed">
                {value.kind === 'kept' ? t('signature.mine') : t(`signature.method.${method}`)}
              </Text>
              <Button variant="subtle" size="compact-sm" onClick={() => onChange(null)}>
                {t('signature.signAgain')}
              </Button>
            </Group>
            {value.kind !== 'kept' && !kept && keepable(method) && (
              <Checkbox
                size="sm"
                checked={value.keep}
                onChange={(e) => onChange({ ...value, keep: e.currentTarget.checked })}
                label={t('signature.keepAsMine')}
              />
            )}
          </>
        ) : handoff.task ? (
          <HandoffStatus handoff={handoff} />
        ) : kept ? (
          <Group grow gap="sm">
            <Button size="md" variant="light" leftSection={<IconSignature size={20} />} onClick={() => onChange({ kind: 'kept' })}>
              {t('signature.useMine')}
            </Button>
            <Button size="md" variant="default" leftSection={<IconWritingSign size={20} />} onClick={() => setSigning(true)}>
              {t('signature.signAgain')}
            </Button>
            {onScreen}
          </Group>
        ) : (
          <Stack gap="sm">
            <button type="button" className={classes.tap} data-error={error ? true : undefined} onClick={() => setSigning(true)}>
              <IconWritingSign size={22} />
              {t('signature.tapToSign')}
            </button>
            {onScreen}
          </Stack>
        )}
      </div>
      {dialog}
      {gate.dialog}
    </Input.Wrapper>
  )
}

/** บัญชีของฉัน: my kept signature, set or changed by a photo of it on paper (`keepableSignatureMethods`), or removed */
export function MySignatureCard() {
  const { t, i18n } = useTranslation()
  const kept = useMySignature()
  const keep = useKeepSignature()
  const forget = useForgetSignature()
  const [signing, setSigning] = useState(false)
  const item = kept.data
  const fail = () => notifications.show({ color: 'red', message: t('signature.failed') })
  const remove = () =>
    modals.openConfirmModal({
      title: t('signature.remove'),
      children: (
        <Text size="sm" c="dimmed">
          {t('signature.removeConfirm')}
        </Text>
      ),
      labels: { confirm: t('signature.remove'), cancel: t('actions.cancel') },
      confirmProps: { color: 'danger' },
      onConfirm: () =>
        forget.mutateAsync().then(
          () => notifications.show({ message: t('signature.removed') }),
          () => fail(),
        ),
    })

  return (
    <SectionCard title={t('signature.mine')}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('signature.mineHint')}
        </Text>
        {item ? (
          <Stack gap={6}>
            <SignatureImage fileId={item.fileId} />
            <Text size="xs" c="dimmed">
              {t(`signature.method.${item.method}`)} ·{' '}
              {t('signature.updated', { at: formatDateTime(item.updatedAt, { locale: currentLocale(i18n), style: 'short' }) })}
            </Text>
            {!keepable(item.method) && (
              <Text size="xs" c="var(--tnn-tone-warning-fg)">
                {t('signature.drawnKept')}
              </Text>
            )}
          </Stack>
        ) : (
          kept.isSuccess && (
            <button type="button" className={classes.tap} onClick={() => setSigning(true)}>
              <IconWritingSign size={22} />
              {t('signature.set')}
            </button>
          )
        )}
        {item && (
          <Group gap="sm">
            <Button variant="light" leftSection={<IconWritingSign size={18} />} onClick={() => setSigning(true)}>
              {t('signature.change')}
            </Button>
            <Button variant="subtle" color="danger" onClick={remove}>
              {t('signature.remove')}
            </Button>
          </Group>
        )}
      </Stack>
      <SignatureDialog
        opened={signing}
        onClose={() => setSigning(false)}
        title={item ? t('signature.change') : t('signature.set')}
        methods={keepableSignatureMethods}
        onConfirm={(signature) =>
          keep.mutateAsync(signature).then(
            () => notifications.show({ message: t('signature.saved') }),
            (error: unknown) => {
              fail()
              throw error
            },
          )
        }
      />
    </SectionCard>
  )
}

/** Before issuing what one signs: a word when one keeps no signature yet (its line is left to sign by hand) */
export function SignatureReminder() {
  const { t } = useTranslation()
  const kept = useMySignature()
  if (!kept.isSuccess || kept.data) return null
  return (
    <Text size="xs" c="dimmed">
      {t('signature.reminder')}{' '}
      <Anchor href={appLink('portal', '/account')} size="xs">
        {t('signature.set')}
      </Anchor>
    </Text>
  )
}

/**
 * A signature given on a document: the image (signed on paper, the photo of the page, opened full size), who and when,
 * how — and "แก้หลังเซ็น" once what the document says changed after it
 */
export function SignedBy({ signature, caption }: { signature: Signature; caption?: ReactNode }) {
  const { t, i18n } = useTranslation()
  return (
    <Stack gap={6}>
      {(caption || signature.changed) && (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          {caption && (
            <Text size="sm" fw={500}>
              {caption}
            </Text>
          )}
          {signature.changed && <StatusPill size="sm" tone="warning" label={t('signing.changed')} />}
        </Group>
      )}
      {signature.method === 'paper' ? (
        <Anchor href={fileUrl(signature.fileId)} target="_blank" rel="noopener" className={classes.pagePhoto}>
          <img src={fileUrl(signature.fileId)} alt={t('signing.pagePhoto')} />
        </Anchor>
      ) : (
        <SignatureImage fileId={signature.fileId} />
      )}
      {signature.changed && (
        <Text size="xs" c="dimmed">
          {t('signing.changedHint')}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {[
          signature.name,
          formatDateTime(signature.signedAt, { locale: currentLocale(i18n), style: 'short' }),
          t(`signature.method.${signature.method}`),
          signature.via !== 'app' ? t(`signature.via.${signature.via}`) : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>
    </Stack>
  )
}
